import { useEffect, useRef, useState } from 'react';

/** Time to ease the displayed bar toward a new firmware percent when it jumps ahead. */
const DEPLOY_PROGRESS_SMOOTH_MS = 400;

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value));

/**
 * Eases deploy progress toward Redux/SDK targets so large firmware jumps (e.g. 1% → 48%)
 * animate smoothly. Completes at 100% immediately; decreases snap immediately.
 */
export function useSmoothedDeployProgress(target: number | null): number | null {
  const [displayed, setDisplayed] = useState<number | null>(target);
  const displayedRef = useRef(target);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (target == null) {
      displayedRef.current = null;
      setDisplayed(null);
      return;
    }

    if (target >= 100) {
      displayedRef.current = 100;
      setDisplayed(100);
      return;
    }

    const from = displayedRef.current;
    if (from == null) {
      displayedRef.current = target;
      setDisplayed(target);
      return;
    }

    const delta = target - from;
    if (delta <= 0) {
      displayedRef.current = clampPercent(target);
      setDisplayed(clampPercent(target));
      return;
    }

    if (delta < 2) {
      displayedRef.current = clampPercent(target);
      setDisplayed(clampPercent(target));
      return;
    }

    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / DEPLOY_PROGRESS_SMOOTH_MS);
      const next = clampPercent(from + delta * easeOutCubic(t));
      displayedRef.current = next;
      setDisplayed(next);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      displayedRef.current = clampPercent(target);
      setDisplayed(clampPercent(target));
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [target]);

  return displayed;
}
